import {Component, HostListener, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {JsonEditorComponent} from "ang-jsoneditor";
import {ActivatedRoute, Router} from "@angular/router";
import {TranslocoService} from "@ngneat/transloco";
import {AuthGuard} from "../../../../common/auth-guard.service";
import {MessageService} from "primeng/api";
import {CommonMessagesService} from "../../../../services/common-messages.service";
import {AssetLoaderService} from "../../../../services/asset-loader.service";
import {JsonEditorService} from "../../../../services/json-editor.service";
import {noSpecialCharacters} from "../../../../shared/custom-validators";
import {DefaultEncoderPersistRequest} from "../../../../model/requests/default-encoder-persist-request";
import {DefaultEncoder} from "../../../../model/default-encoder";
import {DefaultEncodersService} from "../../../../services/default-encoders.service";

@Component({
  selector: 'app-default-encoders-form',
  templateUrl: './default-encoders-form.component.html',
  styleUrls: ['./default-encoders-form.component.scss']
})
export class DefaultEncodersFormComponent implements OnInit {

  private readonly commandsSchemaAssetPath = 'form-config/encoders/commands-schema.json';

  public defaultEncoder: DefaultEncoder;
  public defaultEncoderForm: FormGroup;
  public persistingChanges: any;
  public formSuccess: boolean;

  public mode: 'create' | 'edit';
  public editInfo: { oldName: string };

  @ViewChild('commandsSchemaEditor', {static: false})
  commandsSchemaEditor: JsonEditorComponent;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private i18n: TranslocoService,
    private authGuard: AuthGuard,
    private defaultEncodersService: DefaultEncodersService,
    private messageService: MessageService,
    private commonMessagesService: CommonMessagesService,
    private assetLoaderService: AssetLoaderService,
    public jsonEditorService: JsonEditorService
  ) {
  }

  ngOnInit(): void {
    this.route.url.subscribe(urlSegment => {
      if (urlSegment[1].path === 'new') {
        this.mode = 'create';
        this.loadCreationForm();
      } else {
        this.mode = 'edit';
        this.loadEditionForm(urlSegment[1].path);
      }
    });
  }

  loadCreationForm(): void {
    this.defaultEncoder = new DefaultEncoder();
    this.createForm(this.defaultEncoder);
  }

  loadEditionForm(name): void {
    this.defaultEncodersService.getDefaultEncoder(name).toPromise().then(response => {
      this.defaultEncoder = response.defaultEncoder;
      this.editInfo = {oldName: this.defaultEncoder.name};
      this.defaultEncoder.commandsSchema = JSON.parse(this.defaultEncoder.commandsSchema);
      this.createForm(this.defaultEncoder);
    });
  }

  private createForm(defaultEncoder: DefaultEncoder): void {
    this.defaultEncoderForm = this.formBuilder.group({
      name: [defaultEncoder.name, [Validators.required, Validators.maxLength(255), noSpecialCharacters]],
      commandsSchema: [defaultEncoder.commandsSchema, [Validators.required, Validators.maxLength(255)]]
    });
    this.defaultEncoderForm.markAsPending();

    this.assetLoaderService.loadAssetWithRoute(this.commandsSchemaAssetPath).toPromise().then(config => {
      this.commandsSchemaEditor.setSchema(config.schema, {});
      if (this.mode === 'create') {
        this.defaultEncoderForm.get('commandsSchema').setValue(config.template);
      }
    });
  }

  isCommandsSchemaValid(): boolean {
    if (this.commandsSchemaEditor.isValidJson()) {
      return this.commandsSchemaEditor.getEditor().validateSchema.errors === null;
    }
    return false;
  }

  persistDefaultEncoderData(): void {
    if (!this.defaultEncoderForm.valid) {
      return;
    }

    if (!this.isCommandsSchemaValid()) {
      const summary = this.i18n.translate('error.summary.configErrors');
      const detail = this.i18n.translate('error.jsonEditor.configErrors');
      this.messageService.add({severity: 'error', summary, detail, sticky: true});
      return;
    }

    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    let promise;
    if (this.mode === 'create') {
      promise = this.defaultEncodersService.createDefaultEncoder(persistData).toPromise();
    } else {
      promise = this.defaultEncodersService.editDefaultEncoder(persistData).toPromise();
    }

    promise.then(response => {
      this.formSuccess = true;
      this.router.navigate([`/system/hw-catalog`], {state: {showView: 'encoders'}});
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name);
      this.persistingChanges = false;
    });
  }

  private buildPersistData(): DefaultEncoderPersistRequest {
    const formValue = this.defaultEncoderForm.getRawValue();
    return {
      name: formValue.name,
      commandsSchema: JSON.stringify(formValue.commandsSchema),
      oldName: this.mode === 'edit' ? this.editInfo.oldName : null
    };
  }

  public cancel(): void {
    this.router.navigate([`/system/hw-catalog`], {state: {showView: 'encoders'}});
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || this.defaultEncoderForm.untouched;
  }

}
